const ADMIN_AUTH_KEY = "adminAuth";
const ADMIN_PROFILE_KEY = "adminProfile";
const ACCESS_TOKEN_KEY = "accessToken";

/** Normalize to array of paths (each starting with /). */
function normalizeModules(v) {
  if (Array.isArray(v)) {
    return v.map((p) => (String(p).startsWith("/") ? p : `/${p}`)).filter(Boolean);
  }
  if (v == null) return [];
  const s = String(v).trim();
  if (!s) return [];
  return s
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => (p.startsWith("/") ? p : `/${p}`));
}

/** Normalize to array of city strings. */
function normalizeCities(v) {
  if (Array.isArray(v)) return v.map(String).filter(Boolean);
  if (v == null) return [];
  const s = String(v).trim();
  if (!s) return [];
  return s.split(",").map((c) => c.trim()).filter(Boolean);
}

export function getAdminAuth() {
  const raw = localStorage.getItem(ADMIN_AUTH_KEY);
  if (raw === "true") return true;
  try {
    const parsed = JSON.parse(raw);
    return parsed && Array.isArray(parsed.access_modules) && parsed.access_modules.length > 0;
  } catch {
    return false;
  }
}

/** Allowed module paths, or null for full access. */
export function getAdminAccessModules() {
  const raw = localStorage.getItem(ADMIN_AUTH_KEY);
  if (raw === "true") return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.access_modules) && parsed.access_modules.length > 0) {
      return parsed.access_modules;
    }
  } catch (_) {}
  return null;
}

/**
 * Routes not listed in admin.menuItems but allowed when a related module is granted.
 * e.g. add/edit real wedding is only reachable from Real Weddings list.
 */
const ADMIN_ACCESS_CHILD_ROUTES = [
  {
    test: (p) => p === "/admin/add-real-wedding" || p.startsWith("/admin/add-real-wedding/"),
    parentPath: "/admin/real-weddings",
  },
  {
    test: (p) => p === "/admin/add-blog" || p.startsWith("/admin/add-blog/"),
    parentPath: "/admin/blogs",
  },
];

/** True if pathname is allowed for restricted admins (access_modules list). */
export function isAdminPathAllowed(pathname, allowedModules) {
  if (!allowedModules?.length) return true;
  const path = String(pathname || "").split("?")[0];
  if (allowedModules.includes(path)) return true;
  return ADMIN_ACCESS_CHILD_ROUTES.some(
    ({ test, parentPath }) => test(path) && allowedModules.includes(parentPath)
  );
}

/** Allowed cities, or null. */
export function getAdminCities() {
  const raw = localStorage.getItem(ADMIN_AUTH_KEY);
  if (raw === "true") return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.access_modules) && parsed.access_modules.length > 0) {
      const c = parsed.cities;
      if (Array.isArray(c) && c.length > 0) return c;
      if (c != null && String(c).trim()) return normalizeCities(c);
    }
  } catch (_) {}
  return null;
}

export function setAdminAuth(value) {
  if (value === true || value == null) {
    localStorage.setItem(ADMIN_AUTH_KEY, "true");
    return;
  }
  const access_modules = normalizeModules(value.access_modules);
  const cities = normalizeCities(value.cities ?? value.city);
  if (access_modules.length === 0) {
    localStorage.setItem(ADMIN_AUTH_KEY, "true");
    return;
  }
  localStorage.setItem(ADMIN_AUTH_KEY, JSON.stringify({ access_modules, cities }));
}

export function clearAdminAuth() {
  localStorage.removeItem(ADMIN_AUTH_KEY);
  localStorage.removeItem(ADMIN_PROFILE_KEY);
}

/** Get stored admin profile (email, name) set at login. */
export function getAdminProfile() {
  try {
    const raw = localStorage.getItem(ADMIN_PROFILE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/** Set admin profile (email, name, role) when admin logs in. */
export function setAdminProfile(profile) {

  if (!profile || (typeof profile.email !== "string" && typeof profile.name !== "string")) return;
  localStorage.setItem(ADMIN_PROFILE_KEY, JSON.stringify({
    email: profile.email ?? "",
    name: profile.name ?? "",
    role: profile.role ?? "",
    id: profile.id ?? "",
  }));
}

/** True if the current admin has superadmin role (only they get export Excel, etc.). */
export function isSuperAdmin() {
  const profile = getAdminProfile();
  const role = (profile?.role ?? "").toString().toLowerCase().trim();
  return role === "superadmin";
}

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
export const setAccessToken = (token) =>
  token ? localStorage.setItem(ACCESS_TOKEN_KEY, token) : null;
export const clearAccessToken = () => localStorage.removeItem(ACCESS_TOKEN_KEY);
